// Scoped-only styling: returns an MUI `sx` object meant to be spread onto the
// wrapper element around <RevoGrid>. Nothing here is global and nothing touches
// the shared theme — every value is *read* from the live MUI theme, so the grid
// follows customer branding and light/dark switching automatically.
//
// RevoGrid renders into light DOM (no shadow root) and exposes a public
// `--revo-grid-*` custom property for each of its internal `--rg-theme-*`
// tokens, so setting them on an ancestor is enough — they cascade in.
import { Theme, alpha } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

/** Header height, in px. Also feeds RevoGridTable's height calculation. */
export const REVO_GRID_HEADER_SIZE = 48;

/** Matches a `size="small"` MUI table row (37px input + cell padding). */
export const REVO_GRID_ROW_SIZE = 52;

export const revoGridTableSx = (theme: Theme): SystemStyleObject<Theme> => ({
  // ---- typography ----------------------------------------------------------
  // `fontFamily` is optional on MUI's Typography type, but a custom property
  // value cannot be undefined — fall back to the inherited stack
  '--revo-grid-font-family': theme.typography.fontFamily ?? 'inherit',
  '--revo-grid-font-size': theme.typography.body2.fontSize as string, // 0.875rem / 14px

  // ---- surfaces & text -----------------------------------------------------
  '--revo-grid-background': theme.palette.background.default,
  '--revo-grid-foreground': theme.palette.text.primary,
  '--revo-grid-text': theme.palette.text.primary,
  '--revo-grid-divider': theme.palette.divider,
  '--revo-grid-shadow': alpha(theme.palette.grey[500], 0.16),
  '--revo-grid-border': theme.palette.divider,

  // ---- header: mirrors MuiTableCell `head` ---------------------------------
  '--revo-grid-header-bg': theme.palette.background.neutral,
  '--revo-grid-header-color': theme.palette.text.secondary,
  '--revo-grid-header-height': `${REVO_GRID_HEADER_SIZE}px`,
  '--revo-grid-header-font-size': theme.typography.body2.fontSize as string,
  '--revo-grid-header-font-weight': `${theme.typography.fontWeightMedium}`,
  '--revo-grid-header-text-transform': 'capitalize',
  '--revo-grid-header-padding': theme.spacing(0, 2),
  '--revo-grid-header-hover-bg': theme.palette.action.hover,
  // In the material theme this token only paints the 1px box around the whole
  // grid. MUI's table has no such frame (the Card provides it), so drop it.
  '--revo-grid-header-border': 'transparent',

  // ---- cells ---------------------------------------------------------------
  '--revo-grid-cell-padding': theme.spacing(0, 2),
  '--revo-grid-cell-vertical-border': 'transparent', // MuiTableCell borderBottom: none
  '--revo-grid-cell-disabled-bg': 'transparent',
  '--revo-grid-row-hover': theme.palette.action.hover,
  '--revo-grid-focused-bg': alpha(theme.palette.primary.main, 0.08),

  // ---- selection / interaction accents -------------------------------------
  '--revo-grid-primary': theme.palette.primary.main,
  '--revo-grid-primary-transparent': alpha(theme.palette.primary.main, 0.9),
  '--revo-grid-selection-bg': alpha(theme.palette.primary.main, 0.08),
  '--revo-grid-selection-border': theme.palette.primary.main,
  '--revo-grid-temporary-selection-border': alpha(theme.palette.primary.main, 0.5),
  '--revo-grid-temporary-range-border': alpha(theme.palette.primary.main, 0.5),
  '--revo-grid-range-handle-bg': theme.palette.primary.main,
  '--revo-grid-autofill-handle-bg': theme.palette.primary.main,
  '--revo-grid-autofill-handle-border': theme.palette.background.default,

  // ---- built-in buttons (used by the filter panel) -------------------------
  '--revo-grid-button-bg': theme.palette.primary.main,
  '--revo-grid-button-text': theme.palette.primary.contrastText,
  '--revo-grid-button-outline-border': theme.palette.divider,
  '--revo-grid-button-outline-text': theme.palette.text.primary,
  '--revo-grid-button-success-bg': theme.palette.success.main,
  '--revo-grid-button-danger-bg': theme.palette.error.main,

  // ---- filter panel (RevoGrid's own popover) -------------------------------
  '--revo-grid-filter-panel-bg': theme.palette.background.default,
  '--revo-grid-filter-panel-border': theme.palette.divider,
  '--revo-grid-filter-panel-divider': theme.palette.divider,
  '--revo-grid-filter-panel-shadow': alpha(theme.palette.grey[500], 0.24),
  '--revo-grid-filter-panel-input-bg': theme.palette.background.neutral,
  '--revo-grid-filter-panel-text': theme.palette.text.primary,
  '--revo-grid-filter-panel-muted-text': theme.palette.text.secondary,
  '--revo-grid-filter-panel-icon': theme.palette.action.active,
  '--revo-grid-filter-panel-icon-active': theme.palette.primary.main,
  '--revo-grid-filter-panel-select-border': theme.palette.divider,
  '--revo-grid-filter-panel-select-border-hover': theme.palette.primary.main,
  '--revo-grid-filter-panel-focus-ring': `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,

  // ---- things with no custom property: plain scoped CSS --------------------
  // `revo-grid` is `height: 100%; min-height: 300px` and paints its own 1px
  // frame, so the wrapper owns the height and the frame is dropped here.
  '& revo-grid': {
    height: '100%',
    border: 'none',
  },
  '& revogr-header .header-rgRow': {
    backgroundColor: theme.palette.background.neutral,
  },

  '& revogr-data .rgRow:hover .rgCell': {
    backgroundColor: theme.palette.action.hover,
  },
  // The default cell editor is an unstyled native <input>; give it at least the
  // MUI outlined-input geometry and focus colour.
  '& revogr-edit input, & revogr-edit .edit-input-wrapper input': {
    font: 'inherit',
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: `${theme.shape.borderRadius}px`,
    outline: 'none',
    boxSizing: 'border-box',
  },
});
